import CheckIcon from "@mui/icons-material/Check";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import { GridFilterModel, GridFilterItem } from "@mui/x-data-grid-pro";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { STATUS_MAP, StatusCode } from "@/models/MaintenanceWorkOrderJob";

type Props = {
  filterModel: GridFilterModel;
  setFilterModel: (fm: GridFilterModel) => void;
};

// ----- presets -----
const STATUS_PRESETS = {
  open: [1],
  active: [1, 2, 3, 4],
  completed: [5],
  cancelled: [6],
} as const;

const DATE_PRESETS = [
  { key: "today", labelKey: "common.today", fromDays: 0, toDays: 0 },
  { key: "7d", labelKey: "common.last7days", fromDays: 6, toDays: 0 },
  { key: "30d", labelKey: "common.last30days", fromDays: 29, toDays: 0 },
] as const;

function startOfDay(d: Date) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}
function endOfDay(d: Date) {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}
function isoToLocalDate(iso?: string) {
  return iso ? new Date(iso) : undefined;
}
function datesAlmostEqual(a?: Date, b?: Date, msTolerance = 60_000) {
  if (!a || !b) return false;
  return Math.abs(a.getTime() - b.getTime()) <= msTolerance;
}

// Read current status filter from filterModel
function readStatusCodes(items: GridFilterItem[]): number[] | null {
  const it = items.find(
    i => i.field === "statusCode" && i.operator === "isAnyOf" && i.value != null,
  );
  if (!it) return null;
  const v = Array.isArray(it.value) ? it.value : [it.value];
  return v.map(Number).filter(n => Number.isFinite(n));
}

// Read current createdAt range from filterModel
function readDateRange(items: GridFilterItem[]) {
  const from = items.find(i => i.field === "createdAt" && i.operator === "onOrAfter")?.value as
    | string
    | undefined;
  const to = items.find(i => i.field === "createdAt" && i.operator === "onOrBefore")?.value as
    | string
    | undefined;
  return { fromISO: from, toISO: to, from: isoToLocalDate(from), to: isoToLocalDate(to) };
}

// Figure out which preset (if any) the current date range matches
function detectDatePresetKey(from?: Date, to?: Date): "today" | "7d" | "30d" | null {
  if (!from || !to) return null;
  const now = new Date();

  for (const p of DATE_PRESETS) {
    const presetFrom = startOfDay(new Date(now));
    presetFrom.setDate(presetFrom.getDate() - p.fromDays);
    const presetTo = endOfDay(new Date(now));
    // same-day ranges are handled via fromDays/toDays as above

    if (
      datesAlmostEqual(startOfDay(from), presetFrom) &&
      datesAlmostEqual(endOfDay(to), presetTo)
    ) {
      return p.key;
    }
  }
  return null;
}

export function QuickFilters({ filterModel, setFilterModel }: Props) {
  const { t } = useTranslation();

  // ----- derive active state from filterModel (controlled) -----
  const active = useMemo(() => {
    const items = filterModel.items ?? [];
    const statusCodes = readStatusCodes(items);

    let activeStatusKey: keyof typeof STATUS_PRESETS | null = null;
    if (statusCodes && statusCodes.length) {
      (Object.keys(STATUS_PRESETS) as Array<keyof typeof STATUS_PRESETS>).forEach(key => {
        const preset = STATUS_PRESETS[key];
        if (preset.length === statusCodes.length && preset.every(c => statusCodes.includes(c))) {
          activeStatusKey = key;
        }
      });
    }

    const { from, to } = readDateRange(items);
    const dateKey = detectDatePresetKey(from, to);
    const hasCustomDate = !!from && !!to && !dateKey;

    return { activeStatusKey, dateKey, hasCustomDate, statusCodes };
  }, [filterModel]);

  // ----- labels for status preset chips -----
  const statusChips = useMemo(() => {
    const labels = {
      open: t(`maintenance.status.${STATUS_MAP[1 as StatusCode]}`),
      active: t("maintenance.active"),
      completed: t(`maintenance.status.${STATUS_MAP[5 as StatusCode]}`),
      cancelled: t(`maintenance.status.${STATUS_MAP[6 as StatusCode]}`),
    };
    return [
      { key: "open", label: labels.open, codes: STATUS_PRESETS.open },
      { key: "active", label: labels.active, codes: STATUS_PRESETS.active },
      { key: "completed", label: labels.completed, codes: STATUS_PRESETS.completed },
      { key: "cancelled", label: labels.cancelled, codes: STATUS_PRESETS.cancelled },
    ] as const;
  }, [t]);

  // ----- helpers to apply/remove the filters we own -----
  const upsertFilters = (statusCodes?: number[], dateKey?: string | null) => {
    const others = (filterModel.items ?? []).filter(
      it =>
        !(
          it.field === "statusCode" ||
          (it.field === "createdAt" &&
            (it.operator === "onOrAfter" || it.operator === "onOrBefore"))
        ),
    );
    const items: GridFilterItem[] = [...others];

    if (statusCodes?.length) {
      items.push({
        id: "status-anyOf",
        field: "statusCode",
        operator: "isAnyOf",
        value: statusCodes,
      } as any);
    }

    if (dateKey) {
      const now = new Date();
      const def = DATE_PRESETS.find(d => d.key === dateKey)!;
      const from = startOfDay(new Date(now));
      from.setDate(from.getDate() - def.fromDays);
      const to = endOfDay(new Date(now));

      items.push({
        id: "createdAt-from",
        field: "createdAt",
        operator: "onOrAfter",
        value: from.toISOString(),
      } as any);
      items.push({
        id: "createdAt-to",
        field: "createdAt",
        operator: "onOrBefore",
        value: to.toISOString(),
      } as any);
    }

    setFilterModel({ ...filterModel, items });
  };

  const toggleStatus = (key: keyof typeof STATUS_PRESETS, codes: number[]) => {
    const nextActive = active.activeStatusKey === key ? null : key;
    upsertFilters(nextActive ? codes : undefined, active.dateKey);
  };

  const toggleDate = (key: (typeof DATE_PRESETS)[number]["key"]) => {
    const nextKey = active.dateKey === key ? null : key;
    upsertFilters(
      active.activeStatusKey ? STATUS_PRESETS[active.activeStatusKey] : undefined,
      nextKey,
    );
  };

  const clearManaged = () => {
    const items = (filterModel.items ?? []).filter(
      it =>
        !(
          it.field === "statusCode" ||
          (it.field === "createdAt" &&
            (it.operator === "onOrAfter" || it.operator === "onOrBefore"))
        ),
    );
    setFilterModel({ ...filterModel, items });
  };

  return (
    <Stack direction="row" gap={2} alignItems="center" sx={{ width: "100%", flexWrap: "wrap" }}>
      {/* Status presets */}
      <Stack direction="row" gap={1} alignItems="center">
        {statusChips.map(p => {
          const isActive = active.activeStatusKey === p.key;
          return (
            <Chip
              key={p.key}
              label={p.label}
              onClick={() => toggleStatus(p.key as any, p.codes as any)}
              variant={isActive ? "filled" : "outlined"}
              color={isActive ? "primary" : "default"}
              icon={isActive ? <CheckIcon /> : undefined}
            />
          );
        })}
      </Stack>

      <Divider flexItem orientation="vertical" />

      {/* Date presets */}
      <Stack direction="row" gap={1} alignItems="center">
        {DATE_PRESETS.map(p => {
          const isActive = active.dateKey === p.key;
          return (
            <Chip
              key={p.key}
              label={t(p.labelKey, p.key)}
              onClick={() => toggleDate(p.key)}
              variant={isActive ? "filled" : "outlined"}
              color={isActive ? "primary" : "default"}
              icon={isActive ? <CheckIcon /> : undefined}
            />
          );
        })}
        {/* If restored filters don't match a preset, show "Custom" indicator */}
        {active.hasCustomDate && (
          <Chip
            size="small"
            label={t("common.custom", "Custom")}
            color="primary"
            variant="outlined"
          />
        )}
      </Stack>

      <Divider flexItem orientation="vertical" />

      <Tooltip title={t("common.clear", "Clear")}>
        <IconButton onClick={clearManaged} size="small">
          <ClearAllIcon />
        </IconButton>
      </Tooltip>
    </Stack>
  );
}
